import React, {useEffect} from 'react';
import {Grid, Typography} from '@mui/material';
import {GetBulkAccounts_bulkAccounts_edges_node} from '../../schema-types/GetBulkAccounts';
import DetailPanelContentText, {
  TYPOGRAPHY_MARGIN,
} from '../../components/DetailPanelContentText/DetailPanelContentText';
import {newUTCDate} from '../../utils/newUTCDate';
import {convertBooleanToCheckOrCross} from '../../utils/convertBooleanToCheckOrCross';
import {useBulkAdmin} from '../../features/bulkAdmin/useBulkAdmin';
import {useRTQ} from '../../hooks/useRTQ';
import {Scalars} from '../../graphql/scalars';

const dateOrNA = (d?: Scalars['DateTime']['output'] | null) =>
  d! ? newUTCDate(d).toLocaleDateString() : 'N/A';

const BulkAdminDetailPanelContentBoolean: React.FC<{
  label: string;
  value?: boolean | null;
}> = ({label, value}) => (
  <Grid item>
    <Typography margin={TYPOGRAPHY_MARGIN}>
      <b>{`${label}: `}</b>
      {convertBooleanToCheckOrCross(!!value)}
    </Typography>
  </Grid>
);

export type ActualBulkAdminRow =
  | GetBulkAccounts_bulkAccounts_edges_node
  | null
  | undefined;
export const BulkAdminDetailPanelContent: React.FC<{row: ActualBulkAdminRow}> = ({
  row,
}) => {
  const {atrName} = useRTQ();
  useEffect(() => {
    if (!row?.accountId) {
      return;
    }
    pullAccount({
      variables: {
        id: row.accountId,
      },
    });
  }, [row?.accountId]);

  const {pullAccount, rowData, rowLoading} = useBulkAdmin();
  const client = rowData?.contacts?.[0];

  return (
    <>
      <Grid container borderBottom="solid 1px black" bgcolor="white">
        <DetailPanelContentText
          label="Account Name"
          value={rowData?.name ?? row?.accountName}
        />
        <DetailPanelContentText
          label="Client Name"
          value={client?.name ?? row?.clientName}
        />
      </Grid>
      <Grid container borderBottom="solid 1px black" bgcolor="white">
        <DetailPanelContentText
          label="Proposition Type"
          value={rowData?.propositionType}
          loading={rowLoading}
        />
        <DetailPanelContentText
          label="Adviser"
          value={rowData?.adviser?.name ?? row?.adviser?.name}
        />
        <DetailPanelContentText
          label="Owner"
          value={rowData?.owner?.name ?? row?.owner?.name}
        />
      </Grid>
      <Grid container borderBottom="solid 1px black" bgcolor="white">
        <BulkAdminDetailPanelContentBoolean
          label={`Can request ${atrName}`}
          value={row?.canRequestAtr?.isAllowed}
        />
        {row?.canRequestAtr?.disallowedReason !== '' && (
          <DetailPanelContentText
            label={`Reason cannot request ${atrName}`}
            value={row?.canRequestAtr?.disallowedReason}
          />
        )}
        <DetailPanelContentText
          label={`${atrName} Date`}
          value={dateOrNA(row?.atrDate)}
        />
        <DetailPanelContentText
          label={`Most recent ${atrName} date sent`}
          value={dateOrNA(row?.atrMostRecentlySent)}
        />
        <DetailPanelContentText
          label={`${atrName} last updated`}
          value={dateOrNA(row?.atrLastUpdated)}
        />
        <DetailPanelContentText
          label={`${atrName} score (on most recent completed ${atrName})`}
          value={row?.atrScore}
        />
      </Grid>
      <Grid container borderBottom="solid 1px black" bgcolor="white">
        <BulkAdminDetailPanelContentBoolean
          label="Can request FPA"
          value={row?.canRequestFpa?.isAllowed}
        />
        {row?.canRequestFpa?.disallowedReason !== '' && (
          <DetailPanelContentText
            label="Reason cannot request FPA"
            value={row?.canRequestFpa?.disallowedReason}
          />
        )}
        <BulkAdminDetailPanelContentBoolean
          label="FPA complete"
          value={row?.fpaComplete}
        />
        <BulkAdminDetailPanelContentBoolean
          label="Can request CQ"
          value={row?.canRequestCq?.isAllowed}
        />
        {row?.canRequestCq?.disallowedReason !== '' && (
          <DetailPanelContentText
            label="Reason cannot request CQ"
            value={row?.canRequestCq?.disallowedReason}
          />
        )}
        <DetailPanelContentText label="CQ updated" value={row?.cqUpdated} />
      </Grid>
      <Grid container bgcolor="white">
        <DetailPanelContentText
          label="Portal Acccess"
          value={row?.portalAccess?.status?.replace('_', ' ')}
        />
        <DetailPanelContentText
          label="Next annual review date"
          value={dateOrNA(row?.nextReviewDate)}
        />
        <DetailPanelContentText
          label="Booked For Date"
          value={dateOrNA(row?.meetingBookedDate)}
        />
      </Grid>
    </>
  );
};
